import { useMemo } from 'react';

import { useResponsiveValue } from '#/hooks/useMediaQuery';
import type { BreakpointName } from '#/lib/breakpoints';
import {
  getMinWidthMediaQuery,
  resolveBreakpointMinWidth,
} from '#/lib/breakpoints';

import type { IndicatorProps } from './Indicator';
import { Indicator } from './Indicator';

type IndicatorSize = NonNullable<IndicatorProps['size']>;

interface ResponsiveIndicatorSizeRule {
  minWidth: BreakpointName | number;
  size: IndicatorSize;
}

export type ResponsiveIndicatorProps = IndicatorProps & {
  defaultSize?: IndicatorSize;
  sizeAtBreakpoints?: ResponsiveIndicatorSizeRule[];
};

interface NormalizedResponsiveIndicatorSizeRule {
  query: string;
  value: IndicatorSize;
}

export function ResponsiveIndicator({
  defaultSize = 'md',
  sizeAtBreakpoints = [],
  ...indicatorProps
}: ResponsiveIndicatorProps) {
  const normalizedRules = useMemo<NormalizedResponsiveIndicatorSizeRule[]>(
    () =>
      [...sizeAtBreakpoints]
        .map((rule) => ({
          minWidth: resolveBreakpointMinWidth(rule.minWidth),
          value: rule.size,
          query: getMinWidthMediaQuery(rule.minWidth),
        }))
        .sort((left, right) => left.minWidth - right.minWidth),
    [sizeAtBreakpoints],
  );

  const size = useResponsiveValue(normalizedRules, defaultSize);

  return <Indicator {...indicatorProps} size={size} />;
}
